import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import * as authApi from './api/authApi';
import * as brandApi from './api/brandApi';
import { UserBrand } from './types/brand';
import { markSuccessfulLogin, TOKEN_REFRESHED_EVENT, SESSION_EXPIRED_EVENT } from './utils/tokenRefreshInterceptor';

export interface User {
  id: string;
  email: string;
  username: string;
  full_name?: string;
  /** EFFECTIVE universal access — true for SuperAdmin AND Customer Success.
   *  Every broad admin gate in the app reads this. */
  is_super_admin: boolean;
  /** Raw SuperAdmin flag — ONLY for the reserved surfaces (superadmin dot
   *  menu, profile Superadmin Controls, emulation). */
  is_true_super_admin: boolean;
  is_customer_success: boolean;
  brands: UserBrand[];
}

/** Normalize role flags off a raw API user payload (see User docs above). */
const roleFlags = (raw: any) => {
  const su = !!raw?.is_super_admin;
  const cs = !!raw?.is_customer_success;
  return { is_super_admin: su || cs, is_true_super_admin: su, is_customer_success: cs };
};

export interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** True once the initial session check has completed; never flips back. */
  initialized: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUserBrands: () => Promise<void>;
  error: string | null;
}

export interface RegisterData {
  email: string;
  username: string;
  password: string;
  full_name?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // True once the INITIAL session check has completed. Unlike `loading`,
  // this never flips back (login/register reuse `loading`, and gating UI on
  // that unmounted the marketing page mid-login — the white-screen bug).
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if user is already logged in on mount
  useEffect(() => {
    checkAuth();

    // Another tab refreshed or cleared the tokens. On a WRITE, just sync
    // state — the old handler re-ran checkAuth (/auth/me), which under the
    // blanket /auth/ refresh exclusion could wipe both tokens and log out
    // EVERY tab whenever one tab refreshed. On REMOVAL, mirror the logout.
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'access_token') {
        if (e.newValue) {
          setToken(e.newValue);
        } else {
          setToken(null);
          setUser(null);
        }
      }
    };

    // This tab's shared refresher minted a fresh token: sync context state
    // so components consuming the context token stop sending the dead one.
    const handleTokenRefreshed = (e: Event) => {
      const newToken = (e as CustomEvent).detail?.token || localStorage.getItem('access_token');
      if (newToken) setToken(newToken);
    };

    // The session is definitively dead (refresh token rejected/absent):
    // flip state so the route guards do a soft bounce to the sign-in sheet
    // with a returnUrl — no page reload, no rotting UI.
    const handleSessionExpired = () => {
      setToken(null);
      setUser(null);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(TOKEN_REFRESHED_EVENT, handleTokenRefreshed);
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(TOKEN_REFRESHED_EVENT, handleTokenRefreshed);
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, []);

  const checkAuth = async () => {
    try {
      const storedToken = localStorage.getItem('access_token');

      // Skip auth check on public routes if there's no token
      // (Don't clear tokens here - they might have just been set during login)
      const publicRoutes = ['/login', '/register', '/forgot-password', '/reset-password', '/welcome', '/accept-invitation', '/legacy'];
      const isOnPublicRoute = publicRoutes.some(route => window.location.pathname.startsWith(route));

      if (!storedToken) {
        setLoading(false);
        return;
      }

      // If on public route with a token, skip the API call but don't clear tokens
      // The token might be freshly set during login
      if (isOnPublicRoute) {
        console.log('AuthContext: On public route with token, skipping API check');
        setLoading(false);
        return;
      }

      const userData = await authApi.getCurrentUser(storedToken);

      // Fetch user's brands
      let brands: UserBrand[] = [];
      try {
        brands = await brandApi.getUserBrands();
      } catch (err) {
        console.error('Failed to fetch user brands:', err);
      }

      setToken(storedToken);
      setUser({
        ...userData,
        ...roleFlags(userData),
        brands,
      });
    } catch (err) {
      console.error('Auth check failed:', err);
      // Only a DEFINITIVE auth rejection kills the session. The /auth/me
      // call now refreshes-and-retries through the interceptor, so a 401
      // surfacing here means the refresh token is dead too. Network errors
      // (backend restarting, offline) keep the stored session — the next
      // successful request heals it. The old unconditional wipe here was
      // the "reload after 30 minutes = logged out" bug's second half.
      const status = (err as any)?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setToken(null);
      }
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  const login = async (username: string, password: string) => {
    try {
      setError(null);
      setLoading(true);

      const data = await authApi.login(username, password);

      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);

      // Fetch user's brands after login
      let brands: UserBrand[] = [];
      try {
        brands = await brandApi.getUserBrands();
      } catch (err) {
        console.error('Failed to fetch user brands:', err);
      }

      setToken(data.access_token);
      setUser({
        ...data.user,
        ...roleFlags(data.user),
        brands,
      });

      // Mark successful login to prevent redirect race conditions
      markSuccessfulLogin();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      setError(null);
      setLoading(true);

      const data = await authApi.register(userData);

      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);

      // Fetch user's brands after registration
      let brands: UserBrand[] = [];
      try {
        brands = await brandApi.getUserBrands();
      } catch (err) {
        console.error('Failed to fetch user brands:', err);
      }

      setToken(data.access_token);
      setUser({
        ...data.user,
        ...roleFlags(data.user),
        brands,
      });

      // Mark successful login to prevent redirect race conditions
      markSuccessfulLogin();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      const storedToken = localStorage.getItem('access_token');
      const storedRefresh = localStorage.getItem('refresh_token');
      if (storedToken) {
        try {
          // Pass the refresh token so the backend revokes it too — without
          // this, "logout" left a live 7-day credential behind
          await authApi.logout(storedToken, storedRefresh || undefined);
        } catch (err) {
          console.error('Logout API call failed:', err);
        }
      }
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshUserBrands = async () => {
    if (!user) return;

    try {
      const brands = await brandApi.getUserBrands();
      setUser({
        ...user,
        brands,
      });
    } catch (err) {
      console.error('Failed to refresh user brands:', err);
      throw err;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    isLoading: loading,
    initialized,
    login,
    register,
    logout,
    refreshUserBrands,
    error
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Default export for easier importing
export default AuthContext;
